import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty, MultiSelect } from "../components/common";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Megaphone, Plus, Trash2, Loader2, Calendar, FileText, Users, Paperclip } from "lucide-react";
import { fileUrl } from "../lib/api";

const CAT_ICON = { Meetings: Calendar, Announcements: Megaphone, Proposals: FileText };
const selCls = "px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none";
const inputCls = "w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none";

export default function NoticeBoard() {
  const { user, meta } = useAuth();
  const [notices, setNotices] = useState([]);
  const [filters, setFilters] = useState({ category: "", districts: [], towns: [], sectors: [] });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [form, setForm] = useState({ title: "", category: "Announcements", body: "", districts: [], towns: [], sectors: [], mine_name: "", broadcast_all: true });

  const canPost = user.role === "buyer" || user.role === "admin";

  const load = useCallback(async () => {
    const params = {};
    if (filters.category) params.category = filters.category;
    if (filters.districts.length) params.district = filters.districts.join(",");
    if (filters.towns.length) params.town = filters.towns.join(",");
    if (filters.sectors.length) params.sector = filters.sectors.join(",");
    const { data } = await api.get("/notices", { params });
    setNotices(data);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const toggleFilter = (key) => (v) =>
    setFilters((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));
  const toggleForm = (key) => (v) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let attach = {};
      if (attachment) {
        const fd = new FormData();
        fd.append("file", attachment);
        const { data } = await api.post("/notices/attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attach = { attachment_id: data.file_id, attachment_path: data.storage_path, attachment_name: data.original_filename };
      }
      const { data } = await api.post("/notices", { ...form, ...attach, mine_name: user.mine_name || form.mine_name });
      toast.success(`Notice posted · notified ${data.notified_count} SMMEs`);
      setOpen(false);
      setForm({ title: "", category: "Announcements", body: "", districts: [], towns: [], sectors: [], mine_name: "", broadcast_all: true });
      setAttachment(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this notice?")) return;
    await api.delete(`/notices/${id}`);
    toast.success("Notice deleted");
    load();
  };

  return (
    <div>
      <PageHeader eyebrow="Community" title="Notice Board"
        subtitle="Meetings, announcements and proposals for the supplier community."
        action={canPost && (
          <button data-testid="notice-post-button" onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 bg-[#F2B50C] hover:bg-[#D99E00] text-[#0A2540] font-semibold px-5 py-2.5 rounded-md transition-colors">
            <Plus className="h-4 w-4" /> Post Notice
          </button>
        )} />

      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 flex flex-wrap gap-3">
        <select data-testid="notice-board-filter-category" className={selCls} value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
          <option value="">All Categories</option>
          {(meta?.notice_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {canPost && (
          <>
            <MultiSelect label="Districts" testid="notice-board-filter-district" className="min-w-[200px]"
              options={meta?.districts || []} selected={filters.districts} onToggle={toggleFilter("districts")} />
            <MultiSelect label="Towns" testid="notice-board-filter-town" className="min-w-[200px]"
              options={meta?.towns || []} selected={filters.towns} onToggle={toggleFilter("towns")} />
            <MultiSelect label="Sectors" testid="notice-board-filter-sector" className="min-w-[200px]"
              options={meta?.sectors || []} selected={filters.sectors} onToggle={toggleFilter("sectors")} />
          </>
        )}
      </div>

      {notices.length === 0 ? (
        <Empty icon={Megaphone} title="No notices" subtitle="Nothing posted for these filters yet." />
      ) : (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {notices.map((n) => {
            const Icon = CAT_ICON[n.category] || Megaphone;
            return (
              <div key={n.id} data-testid={`notice-card-${n.id}`} className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#00427A] bg-[#00427A]/10 px-2.5 py-1 rounded">
                    <Icon className="h-3.5 w-3.5" /> {n.category}
                  </span>
                  {canPost && (
                    <button data-testid={`notice-delete-${n.id}`} onClick={() => remove(n.id)} className="text-slate-400 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <h3 className="font-display font-bold text-slate-900">{n.title}</h3>
                <p className="text-sm text-slate-600 mt-2 flex-1">{n.body}</p>
                {n.attachment_path && (
                  <a data-testid={`notice-attachment-${n.id}`} href={fileUrl(n.attachment_path)} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-[#00427A] hover:underline">
                    <Paperclip className="h-3.5 w-3.5" /> {n.attachment_name || "Download attachment"}
                  </a>
                )}
                <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500">
                  {n.broadcast_all ? <span className="bg-[#F2B50C]/10 text-[#F2B50C] px-2 py-0.5 rounded font-semibold flex items-center gap-1"><Users className="h-3 w-3" /> All SMMEs</span> : null}
                  {n.district && <span className="bg-slate-100 px-2 py-0.5 rounded">{n.district}</span>}
                  {(n.towns || []).map((t) => <span key={t} className="bg-slate-100 px-2 py-0.5 rounded">{t}</span>)}
                  {n.sector && <span className="bg-slate-100 px-2 py-0.5 rounded">{n.sector}</span>}
                  {n.mine_name && <span className="bg-slate-100 px-2 py-0.5 rounded">{n.mine_name}</span>}
                </div>
                <p className="text-[11px] text-slate-400 mt-2">By {n.posted_by_name} · {(n.created_at || "").slice(0, 10)}</p>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Post Notice</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Title</label>
              <input data-testid="notice-title-input" className={inputCls} placeholder="Notice title" value={form.title} onChange={set("title")} required />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Category</label>
              <select data-testid="notice-category-select" className={selCls + " w-full"} value={form.category} onChange={set("category")}>
                {(meta?.notice_categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Details</label>
              <textarea data-testid="notice-body-input" className={inputCls} rows={4} placeholder="Notice details…" value={form.body} onChange={set("body")} required />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input data-testid="notice-broadcast-checkbox" type="checkbox" checked={form.broadcast_all}
                onChange={(e) => setForm({ ...form, broadcast_all: e.target.checked })} className="h-4 w-4 accent-[#00427A]" />
              Broadcast to all SMMEs (otherwise only chosen district, town &amp; sector)
            </label>
            {!form.broadcast_all && (
              <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold text-[#00427A] uppercase tracking-wide">Notification Targeting</p>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Target Districts</p>
                  <MultiSelect label="Districts" testid="notice-district" options={meta?.districts || []}
                    selected={form.districts} onToggle={toggleForm("districts")} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Target Towns / Villages</p>
                  <MultiSelect label="Towns" testid="notice-town" options={meta?.towns || []}
                    selected={form.towns} onToggle={toggleForm("towns")} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Target Sectors</p>
                  <MultiSelect label="Sectors" testid="notice-sector" options={meta?.sectors || []}
                    selected={form.sectors} onToggle={toggleForm("sectors")} />
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Attachment</label>
              <label data-testid="notice-attachment-label" className="inline-flex items-center gap-2 text-sm font-semibold cursor-pointer border border-slate-300 hover:bg-slate-50 px-4 py-2 rounded-md">
                <Paperclip className="h-4 w-4" /> {attachment ? "Change document" : "Attach document (optional)"}
                <input data-testid="notice-attachment-input" type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  onChange={(e) => setAttachment(e.target.files[0])} />
              </label>
              {attachment && (
                <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" /> {attachment.name}
                  <button type="button" onClick={() => setAttachment(null)} className="text-red-500 hover:underline ml-1">remove</button>
                </p>
              )}
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>
              <button data-testid="notice-submit-button" disabled={saving}
                className="inline-flex items-center gap-2 bg-[#F2B50C] hover:bg-[#D99E00] text-[#0A2540] font-semibold px-5 py-2 rounded-md disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Post Notice
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
